import * as THREE from 'three';
import { cellToWorld } from './NavigationSystem';
import type { GridMove, InputDirection, PathCell } from './types';

export interface GridMovementOptions {
  /** Seconds required to travel between adjacent cell centers. */
  stepTime?: number;
}

export interface GridMovementBody {
  position: THREE.Vector3;
}

export interface GridMovementResult {
  /** Cell occupied or reserved by the player after this update. */
  cell: PathCell;
  /** Active transition, or null when the player is exactly on a cell center. */
  move: GridMove | null;
  /** True when a new cell transition was started this frame. */
  started: boolean;
  /** True when the active transition reached its destination this frame. */
  completed: boolean;
}

const DEFAULT_STEP_TIME = 0.18;

const DIRECTIONS: Record<InputDirection, readonly [number, number]> = {
  forward: [0, -1],
  back: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};

/**
 * Moves a player from one cell center to the next without accepting arbitrary
 * world-space offsets. Once a transition starts, it always completes, even if
 * the key is released halfway through the cell.
 */
export class GridMovementSystem {
  private readonly stepTime: number;
  private move: GridMove | null = null;

  constructor(options: GridMovementOptions = {}) {
    this.stepTime = positive(options.stepTime, DEFAULT_STEP_TIME);
  }

  get activeMove(): GridMove | null { return this.move; }

  reset(): void {
    this.move = null;
  }

  update(
    body: GridMovementBody,
    cell: PathCell,
    dt: number,
    direction: InputDirection | undefined,
    canEnter: (col: number, row: number) => boolean,
  ): GridMovementResult {
    let currentCell = cell;
    let started = false;
    let completed = false;

    if (!this.move && direction) {
      const vector = DIRECTIONS[direction];
      const nextCol = currentCell.col + vector[0];
      const nextRow = currentCell.row + vector[1];
      if (canEnter(nextCol, nextRow)) {
        this.move = {
          from: body.position.clone(),
          to: cellToWorld(nextCol, nextRow),
          progress: 0,
          vector,
        };
        currentCell = { col: nextCol, row: nextRow };
        started = true;
      }
    }

    if (this.move) {
      this.move.progress = Math.min(1, this.move.progress + finiteNonNegative(dt) / this.stepTime);
      body.position.lerpVectors(this.move.from, this.move.to, this.move.progress);
      if (this.move.progress >= 1) {
        body.position.copy(this.move.to);
        this.move = null;
        completed = true;
      }
    }

    return { cell: currentCell, move: this.move, started, completed };
  }
}

function finiteNonNegative(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function positive(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) && value > 0 ? value : fallback;
}
